import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
} from '@material-ui/core';
import { Edit, Delete } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import PaginationData from 'types/models/PaginationData';
import AppContext from 'contexts/AppContext';
import EditStockModuleContext from 'contexts/EditStockModuleContext';
import { StockRecipe } from 'types/models/StockMovement';
import { getRecipes, deleteRecipe } from 'services/stockRecipeServices';
import { getSimpleVenuesWithStockModuleActive } from 'services/venuesServices';
import SelectField from 'components/SelectField';
import ConfirmDialog from 'components/ConfirmDialog';
import { getApiErrorMessage } from 'utils/errors';
import SaveError from 'components/common/SaveError';
import { mapToSelectOptions } from 'utils/select';
import Venue from 'types/models/Venue';
import UserRoleType from 'types/enums/UserRoleType';
import useSetupInitialState from 'hooks/useSetupInitialState';

export default function StockRecipesPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { setSelectedRecipe } = React.useContext(EditStockModuleContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [venueId, setVenueId] = React.useState<string>('');
  const [paginationData, setPaginationData] = React.useState<PaginationData<StockRecipe>>();
  const [recipeToDelete, setRecipeToDelete] = React.useState<StockRecipe | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    async function loadVenues() {
      if (isVenueEditor) {
        return;
      }
      try {
        const response = await getSimpleVenuesWithStockModuleActive();
        if (mounted) setVenues(response ?? []);
      } catch (error) {
        console.error(error);
      }
    }

    loadVenues();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  React.useEffect(() => {
    if (!isVenueEditor) return;
    if (!entity?.id) return;
    setVenueId(entity.id);
  }, [isVenueEditor, entity]);

  const loadData = React.useCallback(
    async (page?: number, searchTerm?: string) => {
      try {
        setError(null);
        const paginationData = await getRecipes(page, searchTerm, { venueId });
        if (paginationData) setPaginationData(paginationData);
      } catch (error) {
        setError(getApiErrorMessage(error) ?? t('errors.generic'));
      }
    },
    [venueId, t],
  );

  React.useEffect(() => {
    if (!venueId) {
      setPaginationData(undefined);
      return;
    }
    if (currentPageListNumber) {
      loadData(currentPageListNumber);
    }
  }, [currentPageListNumber, location.search, loadData, venueId]);

  const onConfirmDelete = async () => {
    if (!recipeToDelete) return;

    try {
      await deleteRecipe(recipeToDelete.id);
      setRecipeToDelete(null);
      loadData();
    } catch (error) {
      setError(getApiErrorMessage(error) ?? t('errors.generic'));
    }
  };

  const renderContent = (paginationData: StockRecipe[]): JSX.Element => {
    return (
      <>
        {paginationData.map((recipe) => (
          <TableRow key={recipe.id}>
            <TableCell>{recipe.globalProduct?.name}</TableCell>
            <TableCell className="unique-value-cell">{recipe.globalProduct?.sku}</TableCell>
            <TableCell>
              {recipe.items?.map((item) => (
                <span key={item.id}>
                  {item.ingredient?.name ?? '—'} × {Number(item.quantity)}
                  {item.id !== recipe.items?.[recipe.items.length - 1]?.id ? ', ' : ''}
                </span>
              ))}
            </TableCell>
            <TableCell>
              {renderEditButton(recipe)}
              {renderDeleteButton(recipe)}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const renderEditButton = (recipe: StockRecipe): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (!recipe) return;
          setSelectedRecipe(recipe);
          history.push(`/private/stock/recipes/${recipe.id}/edit`);
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderDeleteButton = (recipe: StockRecipe): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (!recipe) return;
          setRecipeToDelete(recipe);
        }}
      >
        <Delete />
      </IconButton>
    );
  };

  return (
    <>
      {!isVenueEditor && (
        <SelectField
          name="venueId"
          label={t('venue')}
          placeholder={t('select')}
          value={venueId}
          options={mapToSelectOptions(venues, { label: 'name' })}
          setFieldValue={(_, value) => setVenueId(value as string)}
          touched={false}
          error={false}
          isMulti={false}
          isSearchable
          isClearable={false}
          backspaceRemovesValue={false}
          onBlur={() => undefined}
          isDisabled={false}
        />
      )}
      <Button
        className="btn-50 my-6"
        variant="contained"
        color="primary"
        onClick={() => {
          history.push('/private/stock/recipes/create');
        }}
      >
        {t('stock.recipes.create')}
      </Button>

      <ConfirmDialog
        open={recipeToDelete !== null}
        setOpen={(open) => {
          if (!open) {
            setRecipeToDelete(null);
          }
        }}
        title={t('stock.recipes.dialog.delete-title')}
        description={t('stock.recipes.dialog.delete-description')}
        onConfirm={onConfirmDelete}
        onCancel={() => setRecipeToDelete(null)}
      />

      <SaveError error={error} />
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('stock.recipes.table.product')}</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>{t('stock.recipes.table.ingredients')}</TableCell>
              <TableCell>{t('stock.recipes.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationData?.data && paginationData?.data.length > 0 ? (
              renderContent(paginationData.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('stock.recipes.table.no-results')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationData && (
        <TablePagination
          component="div"
          count={paginationData.total}
          page={paginationData.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationData.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
}
